import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { corporateInput, EmptyState, fmtDate } from "@/components/Shared";
import { UserPlus, UsersThree, Trash, ShieldStar, User, ArrowsLeftRight } from "@phosphor-icons/react";
import RowMenu from "@/components/RowMenu";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const blank = { email: "", name: "", password: "", role: "agent" };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const isSupportAdmin = me?.role === "support_admin";
  const isSubAgent = !!me?.created_by_agent;
  const canCreate = isSupportAdmin || (me?.role === "agent" && !isSubAgent);

  const load = async () => {
    try {
      const { data } = await api.get("/support/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/support/users", form);
      toast.success(`Usuario creado: ${form.email}`);
      setOpen(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id, email) => {
    if (!confirm(`¿Eliminar al usuario ${email}?`)) return;
    try { await api.delete(`/support/users/${id}`); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const promote = async (id, currentRole) => {
    const newRole = currentRole === "agent" ? "support_admin" : "agent";
    if (!confirm(`¿Cambiar rol a ${newRole === "support_admin" ? "Admin Support" : "Agente"}?`)) return;
    try { await api.patch(`/support/users/${id}`, { role: newRole }); toast.success("Rol actualizado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PageShell
      section="Support"
      page="Usuarios"
      context={`${users.length} miembros del equipo`}
      testid="users-toolbar"
      actions={canCreate && <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white h-9" data-testid="add-user"><UserPlus size={15} className="mr-1.5" /> {isSupportAdmin ? "Crear usuario" : "Crear sub-agente"}</Button>}
    >
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Usuario</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Rol</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Creado</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.length === 0 && (
              <tr><td colSpan={4}><EmptyState icon={UsersThree} title="Sin usuarios" message="Crea el primer agente o admin de Support." action={<Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white h-9"><UserPlus size={15} className="mr-1.5" /> Crear usuario</Button>} /></td></tr>
            )}
            {users.map((u) => {
              const isMe = u.email === me?.email;
              const isAdmin = u.role === "support_admin";
              return (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full ${isAdmin ? "bg-gradient-to-br from-amber-400 to-orange-500" : "bg-gradient-to-br from-indigo-400 to-indigo-600"} grid place-items-center text-white text-sm font-bold shrink-0`}>
                        {(u.name || u.email).slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <div>{u.name || "—"} {isMe && <span className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-amber-600">(tú)</span>}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {isAdmin ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"><ShieldStar size={10} weight="fill" /> Admin Support</span>
                    ) : u.created_by_agent ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 w-fit"><User size={10} weight="fill" /> Sub-agente</span>
                        <span className="text-[10px] text-slate-400 font-mono">↳ {u.created_by_agent}</span>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"><User size={10} weight="fill" /> Agente</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-500 text-xs font-mono">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {!isMe && (
                      <RowMenu
                        testid={`user-menu-${u.id}`}
                        items={[
                          { label: isAdmin ? "Hacer agente" : "Promover a Admin", icon: ArrowsLeftRight, onClick: () => promote(u.id, u.role), testid: `promote-${u.id}` },
                          { type: "separator" },
                          { label: "Eliminar", icon: Trash, onClick: () => remove(u.id, u.email), testid: `del-user-${u.id}`, danger: true },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Crear usuario de Support</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Nombre completo *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={corporateInput} data-testid="user-name" /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={corporateInput} data-testid="user-email" placeholder="agente@flexenergy.pro" /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Contraseña * (min 6)</Label><PasswordInput value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className={corporateInput} data-testid="user-password" /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Rol *</Label>
              <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className={corporateInput} data-testid="user-role" disabled={!isSupportAdmin}>
                <option value="agent">Agente{!isSupportAdmin ? " (sub-agente bajo tu cuenta)" : ""}</option>
                {isSupportAdmin && <option value="support_admin">Admin de Support</option>}
              </select>
              {!isSupportAdmin && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Nota — Este usuario quedará como sub-agente vinculado a tu cuenta. Podrá gestionar sus propios leads pero <b>no</b> podrá crear más agentes.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={!form.name || !form.email || form.password.length < 6} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-user">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
